import type {
    ContentRef,
    ContentRefFile,
    ContentRefReusableContent,
    ContentRefTag,
    ContentRefURL,
} from '@gitbook/api-client';
import { isUrl } from '@gitbook/util-url';

/*
 * Indepotent content references formatters and parsers.
 */
export function createIndepotentContentRefFormatter(options: { prefix?: string } = {}) {
    const { prefix: rawPrefix } = options;
    const prefix = rawPrefix?.endsWith('/') ? rawPrefix.slice(0, -1) : (rawPrefix ?? '');

    /**
     * Format a content ref to a generic link source.
     */
    const resolveContentRefToSource = (ref: ContentRef): string => {
        const resolver = RESOLVERS[ref.kind] as GenericResolver;
        if (!resolver) {
            throw new Error(`No resolver found for content ref kind: ${ref.kind}`);
        }

        return resolver.format(prefix, ref);
    };

    /**
     * Format a content ref for formatting.
     */
    const resolveContentRef = (ref: ContentRef): { url: string; text: string } => {
        const source = resolveContentRefToSource(ref);
        return { url: source, text: source };
    };

    /**
     * Resolve a link to a content ref.
     */
    const resolveLink = (src: string): ContentRef | null => {
        for (const resolver of Object.values(RESOLVERS)) {
            const ref = resolver.resolve(prefix, src);
            if (ref) {
                return ref;
            }
        }

        return null;
    };

    /**
     * Resolve a source to a file content ref.
     */
    const resolveFile = (src: string): ContentRefFile | ContentRefURL | null => {
        return RESOLVERS.file.resolve(prefix, src) ?? RESOLVERS.url.resolve(prefix, src);
    };

    /**
     * Resolve a source to a reusable content content ref.
     */
    const resolveReusableContent = (src: string): ContentRefReusableContent | null => {
        return RESOLVERS['reusable-content'].resolve(prefix, src);
    };

    return {
        resolveContentRef,
        resolveContentRefToSource,
        resolveLink,
        resolveFile,
        resolveReusableContent,
    };
}

/**
 * Resolve a tag source string to a content reference.
 * - local tag: `beta`
 * - absolute tag: `s/space-id/tags/beta`
 */
export function resolveTagSourceToRef(src: string): ContentRefTag | null {
    const trimmed = src.trim();
    if (!trimmed) {
        return null;
    }

    const absoluteMatch = RESOLVERS.tag.resolve('', trimmed);
    if (absoluteMatch) {
        return absoluteMatch;
    }

    if (trimmed.includes('/')) {
        return null;
    }

    return {
        kind: 'tag',
        tag: trimmed,
    };
}

/**
 * Resolve a tag content reference to a source string.
 * - local tag: `beta`
 * - absolute tag: `/spaces/space-id/tags/beta`
 */
export function resolveTagRefToSource(ref: ContentRefTag): string {
    if (ref.space) {
        return RESOLVERS.tag.format('', ref);
    }

    return ref.tag;
}

type GenericResolver = {
    resolve: (prefix: string, src: string) => ContentRef | null;
    format: (prefix: string, ref: ContentRef) => string;
};

const RESOLVERS: {
    [kind in ContentRef['kind']]: {
        resolve: (prefix: string, src: string) => Extract<ContentRef, { kind: kind }> | null;
        format: (prefix: string, ref: Extract<ContentRef, { kind: kind }>) => string;
    };
} = {
    page: {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^(\/spaces\/([\w-]+))?\/pages\/([\w-]+)$/);
            if (match) {
                return {
                    kind: 'page',
                    ...(match[2] ? { space: match[2] } : {}),
                    page: match[3],
                };
            }

            return null;
        },
        format: (prefix, ref) => {
            const pagePath = `/pages/${ref.page}`;
            if (ref.space) {
                return withPrefix(prefix, `/spaces/${ref.space}${pagePath}`);
            }
            return withPrefix(prefix, pagePath);
        },
    },

    anchor: {
        resolve: (prefix, src) => {
            if (src.startsWith('#')) {
                return {
                    kind: 'anchor',
                    anchor: src.slice(1),
                };
            }

            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^((\/spaces\/([\w-]+))?\/pages\/([\w-]+))?#([\w-]+)$/);
            if (match) {
                return {
                    kind: 'anchor',
                    ...(match[3] ? { space: match[3] } : {}),
                    ...(match[4] ? { page: match[4] } : {}),
                    anchor: match[5],
                };
            }

            return null;
        },
        format: (prefix, ref) => {
            const anchorPath = `#${ref.anchor}`;
            if (!ref.page) {
                return anchorPath;
            }
            if (ref.space && ref.page) {
                return withPrefix(prefix, `/spaces/${ref.space}/pages/${ref.page}${anchorPath}`);
            }
            return withPrefix(prefix, `/pages/${ref.page}${anchorPath}`);
        },
    },

    file: {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^(\/spaces\/([\w-]+))?\/files\/([\w-]+)$/);
            if (match) {
                return {
                    kind: 'file',
                    file: match[3],
                    ...(match[2] ? { space: match[2] } : {}),
                };
            }

            return null;
        },
        format: (prefix, ref) => {
            const filePath = `/files/${ref.file}`;
            if (ref.space) {
                return withPrefix(prefix, `/spaces/${ref.space}${filePath}`);
            }
            return withPrefix(prefix, filePath);
        },
    },

    space: {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^\/spaces\/([\w-]+)$/);
            if (match) {
                return {
                    kind: 'space',
                    space: match[1],
                };
            }

            return null;
        },
        format: (prefix, ref) => withPrefix(prefix, `/spaces/${ref.space}`),
    },

    collection: {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^\/collections\/([\w-]+)$/);
            if (match) {
                return {
                    kind: 'collection',
                    collection: match[1],
                };
            }

            return null;
        },
        format: (prefix, ref) => withPrefix(prefix, `/collections/${ref.collection}`),
    },

    user: {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^\/users\/([\w-]+)$/);
            if (match) {
                return {
                    kind: 'user',
                    user: match[1],
                };
            }

            return null;
        },
        format: (prefix, ref) => withPrefix(prefix, `/users/${ref.user}`),
    },

    'reusable-content': {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^(\/spaces\/([\w-]+))?\/reusable-content\/([\w-]+)$/);
            if (match) {
                return {
                    kind: 'reusable-content',
                    ...(match[2] ? { space: match[2] } : {}),
                    reusableContent: match[3],
                };
            }

            return null;
        },
        format: (prefix, ref) => {
            const reusableContentPath = `/reusable-content/${ref.reusableContent}`;
            if (ref.space) {
                return withPrefix(prefix, `/spaces/${ref.space}${reusableContentPath}`);
            }
            return withPrefix(prefix, reusableContentPath);
        },
    },

    tag: {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^(\/spaces\/([\w-]+))?\/tags\/([\w-]+)$/);
            if (match) {
                return {
                    kind: 'tag',
                    ...(match[2] ? { space: match[2] } : {}),
                    tag: match[3],
                };
            }

            return null;
        },
        format: (prefix, ref) => {
            const tagPath = `/tags/${ref.tag}`;
            if (ref.space) {
                return withPrefix(prefix, `/spaces/${ref.space}${tagPath}`);
            }
            return withPrefix(prefix, tagPath);
        },
    },

    openapi: {
        resolve: (prefix, src) => {
            const relativeSrc = withoutPrefix(prefix, src);
            if (!relativeSrc) {
                return null;
            }
            const match = relativeSrc.match(/^\/openapi\/([^/]+)$/);
            if (match) {
                return {
                    kind: 'openapi',
                    spec: match[1],
                };
            }

            return null;
        },
        format: (prefix, ref) => withPrefix(prefix, `/openapi/${ref.spec}`),
    },

    url: {
        resolve: (_prefix, src) => {
            if (isUrl(src)) {
                return { kind: 'url', url: src };
            }
            return null;
        },
        format: (_prefix, ref) => {
            return ref.url;
        },
    },
};

function withoutPrefix(prefix: string, src: string): string | null {
    const normalizedSrc = src.startsWith('//') ? src.slice(1) : src;

    if (prefix) {
        if (normalizedSrc.startsWith(`${prefix}/`)) {
            return normalizedSrc.slice(prefix.length);
        }
        return null;
    }
    return normalizedSrc;
}

function withPrefix(prefix: string, src: string): string {
    return `${prefix}${src}`;
}
